// store.js
import { defineStore } from 'pinia';
import call_waiting from '@/assets/call_waiting.mp3';
import ringback from '@/assets/ringback.mp3';
import busy from '@/assets/busy.mp3';
import pickup from '@/assets/pickup.mp3';
import { Invitation, Inviter, type InviterInviteOptions, SessionState, UserAgent } from 'sip.js';
import { isElectron } from '@/utils/clientUtils';
import { removeUnnecessaryCharsFromNumber } from '@/utils/numberUtils';
import type { SessionDescriptionHandlerOptions } from 'sip.js/lib/platform/web';
import { computed, reactive, type Ref, type UnwrapRef } from 'vue';
import { type Call, CallEndReason, useCallStore } from '@/stores/useCallStore';
import { useMediaDeviceStore } from '@/stores/useMediaDeviceStore';
import { useAudioStore } from '@/stores/useAudioStore';
import { useSessionStore } from '@/stores/useSessionStore';
import { headsetService } from '@/libraries/headset/headset';
import { useClientSideSettingsStore } from '@/stores/useClientSideSettingsStore';
import { setupRingtone } from '@/utils/audioUtils';
import { sdpModifierHandler } from '@/utils/sdpHandlerUtils';
import { usePhoneConnection, type PhoneStatusType } from '@/composables/usePhoneConnection';

export type { PhoneStatusType };

interface PhoneStore {
  phoneStatus: Ref<PhoneStatusType>;
  createPhone: () => Promise<void>;
  destroyPhone: () => Promise<void>;
  selectOutputForAllCalls: (outputId: string) => void;
  selectInputForAllCalls: (inputId: string | null) => void;
  call: (
    target: string,
    isTransferTarget?: boolean,
    otherPartyOverride?: {
      number: string;
      name?: string;
    } | null
  ) => UnwrapRef<Call | null>;
  getSession: (id: string) => UnwrapRef<Call> | null | undefined;
  activeCalls: Ref<Call[]>;
  isOffline: Ref<boolean>;
  canonizeTarget: (target: string) => string;
  getUserAgent: () => UserAgent | undefined;
}

export const usePhoneStore = defineStore('phone', (): PhoneStore => {
  const callWaitingUrl = call_waiting;
  const ringbackSoundUrl = ringback;
  const busySoundUrl = busy;
  const $session = useSessionStore();
  const $callStore = useCallStore();
  const $mediaDeviceStore = useMediaDeviceStore();
  const $audioStore = useAudioStore();
  const $clientSettingsStore = useClientSideSettingsStore();

  const getRingtoneBasedOnNumber = (call: Call) => {
    return $callStore.callMembers(call)[0]!.number.startsWith('+')
      ? $audioStore.getSelectedExternalRingtone.path
      : $audioStore.getSelectedInternalRingtone.path;
  };

  const onInvite = async (invitation: Invitation) => {
    const hasActiveCall = $callStore.getCalls.some((call) => call.session.state === SessionState.Established && !call.holding);

    const ringtones: HTMLAudioElement[] = [];
    const newSession = $callStore.createNewCall(invitation, true, ringtones, null, false);

    const isPickupGroupCall = newSession.value.isPickupGroupCall;
    const ringtoneUrl = isPickupGroupCall ? pickup : hasActiveCall ? callWaitingUrl : getRingtoneBasedOnNumber(newSession.value);
    const loop = !isPickupGroupCall;
    const mdv = $mediaDeviceStore;

    if (!isPickupGroupCall || (isPickupGroupCall && $clientSettingsStore._settings.playPickupSound && !hasActiveCall)) {
      const primaryRingtone = setupRingtone(ringtoneUrl, loop, mdv.selectedRingtoneOutputId);
      ringtones.push(primaryRingtone);

      if (!hasActiveCall && mdv.ringOnBothDevices && mdv.selectedRingtoneOutputId !== mdv.selectedAudioOutputId) {
        const secondaryRingtone = setupRingtone(ringtoneUrl, loop, mdv.selectedAudioOutputId);
        ringtones.push(secondaryRingtone);
      }
    }

    headsetService?.incomingCall(newSession.value);
    $callStore.addCall(newSession.value);
    ringtones.forEach((r) => {
      r.volume = 1;
      // Swallow benign AbortError if the ringtone is stopped early.
      r.play().catch(() => undefined);
    });
    console.log('Ringing', ringtones);

    if (isElectron()) {
      console.log('Executing script');
      window.wtgPhoneApi.executeIncomingCallScript($callStore.callMembers(newSession.value)[0]!.number);
    }
  };

  const { phoneStatus, isOffline, login, logout, getUserAgent } = usePhoneConnection(onInvite);

  const canonizeTarget = (target: string): string => {
    let newTarget = removeUnnecessaryCharsFromNumber(target);
    if (!newTarget.startsWith('sip:')) {
      newTarget = 'sip:' + newTarget;
    }
    if (!newTarget.includes('@')) {
      newTarget += '@' + $session._session.domain;
    }
    return newTarget;
  };

  const call = (
    target: string,
    isTransferTarget = false,
    otherPartyOverride: { number: string; name?: string } | null = null
  ): UnwrapRef<Call | null> => {
    target = canonizeTarget(target);

    const userAgent = getUserAgent();
    if (userAgent != null) {
      const calleeUri = UserAgent.makeURI(target);
      console.log('Calling uri', calleeUri);
      const inviter = reactive(new Inviter(userAgent, calleeUri!));
      const descriptionHandlerOptions: SessionDescriptionHandlerOptions = {
        iceGatheringTimeout: 1,
        constraints: {
          audio: $mediaDeviceStore.buildAudioConstraints(),
          video: false,
        },
      };

      const ringback = new Audio(ringbackSoundUrl);
      ringback.loop = true;
      if ($mediaDeviceStore.selectedAudioOutputId !== null) {
        if ('setSinkId' in ringback) {
          console.log('Ringback sink id set to', $mediaDeviceStore.selectedAudioOutputId);
          (ringback as any).setSinkId($mediaDeviceStore.selectedAudioOutputId).catch(() => undefined);
        } else {
          console.error("Can't set ringback sink id");
        }
      }
      // Swallow benign AbortError if the ringback is stopped early.
      ringback.play().catch(() => undefined);
      const newSession = $callStore.createNewCall(inviter, false, null, ringback, isTransferTarget, otherPartyOverride);

      const inviterInviteOptions: InviterInviteOptions = {
        sessionDescriptionHandlerOptions: descriptionHandlerOptions,
        sessionDescriptionHandlerModifiers: [sdpModifierHandler],
        requestDelegate: {
          onReject: (response) => {
            console.log('Call ended Rejected: ' + response.message.statusCode + ' (' + response.message.reasonPhrase + ')');
            const rejectedCode = response.message.statusCode ? response.message.statusCode : null;

            if (rejectedCode === 603) {
              console.log('Call ended: Declined');
              newSession.value.endReason = CallEndReason.REMOTE_DECLINED;
              $callStore.playSoundToLocal(busySoundUrl, 0.3, newSession.value);
            } else if (rejectedCode === 500) {
              console.log('Call ended: Server error (timeout?)');
              newSession.value.endReason = CallEndReason.SERVER_ERROR;
              $callStore.playSoundToLocal(busySoundUrl, 0.3, newSession.value);
            } else if (rejectedCode === 486) {
              console.log('Call ended: Busy');
              newSession.value.endReason = CallEndReason.REMOTE_BUSY;
              $callStore.playSoundToLocal(busySoundUrl, 0.3, newSession.value);
            } else if (rejectedCode === 480) {
              console.log('Call ended: Temporarily unavailable');
              newSession.value.endReason = CallEndReason.REMOTE_TEMPORARILY_UNAVAILABLE;
              $callStore.playSoundToLocal(busySoundUrl, 0.3, newSession.value);
            } else if (rejectedCode === 487) {
              console.log('Call ended: Request terminated');
              // This is probably by us, so we'll play the call end sound in the end() function
            } else if (rejectedCode !== null) {
              console.log('Call ended: Unknown code ' + rejectedCode);
              newSession.value.endReason = CallEndReason.UNKNOWN_REJECTION;
              $callStore.playSoundToLocal(busySoundUrl, 0.3, newSession.value);
            }

            // sip.js only transitions the session to Terminated here if its
            // own internal state guard happens to match at this exact
            // moment (see Inviter.onReject() in sip.js) — if it doesn't,
            // this callback still fires (we did receive the rejection) but
            // the call is otherwise left stuck locally (ringback still
            // playing, "calling" UI never clearing). This is a no-op if the
            // normal path already cleaned up.
            $callStore.forceLocalTerminationIfStuck(newSession.value);
          },
        },
      };

      console.log('debug call: before invite');
      inviter.invite(inviterInviteOptions).catch((reason) => {
        console.log('Invite failed, assuming sound error:', reason);
        newSession.value.endReason = CallEndReason.SOUND_ERROR;
      });
      console.log('debug call: after invite');
      $callStore.addCall(newSession.value);
      return newSession.value;
    }
    return null;
  };

  const getSession = (id: string): UnwrapRef<Call> | null | undefined => {
    const lid = id.toLowerCase();
    const results = $callStore.getCalls.filter((call) => call.callId.toLowerCase() === lid);
    if (results.length === 0) {
      console.log('Call with id', id, 'not here:', $callStore.getCalls);
      return null;
    } else {
      console.log('Call with id', id, 'found');
      return results[0];
    }
  };

  const activeCalls = computed((): UnwrapRef<Call>[] => {
    return $callStore.getCalls.filter((call) => call.session.state === SessionState.Established);
  });

  const selectOutputForAllCalls = (outputId: string) => {
    $callStore.getCalls.forEach((call) => {
      $callStore.selectOutput(outputId, call);
    });
  };

  const selectInputForAllCalls = (inputId: string | null) => {
    $callStore.getCalls.forEach((call) => {
      $callStore.selectInput(inputId, call);
    });
  };

  return {
    phoneStatus,
    selectOutputForAllCalls,
    selectInputForAllCalls,
    call,
    getSession,
    activeCalls,
    isOffline,
    canonizeTarget,
    createPhone: login,
    destroyPhone: logout,
    getUserAgent,
  };
});
